package com.sddamico.draftmachine.models

import com.sddamico.draftmachine.data.DraftStatus
import com.sddamico.draftmachine.data.PickOrigin
import kotlinx.serialization.Serializable

/**
 * Frontend mirror of the (new) jvmMain `DraftDetailModels.kt` — the whole draft room in one
 * payload (`GET /api/draft-detail?draftId=`). Names are resolved backend-side in bulk so the
 * client never fans out per-pick lookups.
 */
@Serializable
data class DraftDetailResponse(
  val draft: DraftResponse,
  val leagueId: String? = null,
  val leagueName: String? = null,
  val youManage: Boolean,
  val youCanManage: Boolean = youManage,
  val pickNotificationsEnabled: Boolean = false,
  val draftAdminTeamIds: List<String> = emptyList(),
  val draftAdminEligibleTeamIds: List<String> = emptyList(),
  val yourTeamIds: List<String> = emptyList(),
  val teams: List<TeamSummary> = emptyList(),
  val picks: List<PickDetail> = emptyList(),
  val onClock: OnClockInfo? = null,
  val totalPicks: Int,
  val filledPicks: Int,
  val serverTimeIso: String,
  // True while a SETUP draft's lottery still has unrevealed draws — the manager should resume it.
  val lotteryPending: Boolean = false,
  val pool: PlayerPoolResponse? = null,
  // Set while an off-pool pick awaits a manager/admin ruling; the draft is paused until then.
  val pendingPick: PendingPickInfo? = null,
)

/** Frontend mirror of the jvmMain `PendingPickInfo`. */
@Serializable
data class PendingPickInfo(
  val position: Int,
  val round: Int,
  val teamId: String,
  val teamName: String,
  val playerText: String,
  val enteredAt: String? = null,
)

/**
 * One pick, joined to its team + owner names. [playerText] carries a free-text pick; [playerId]
 * the catalog path (unused in the current free-text flow). [origin]/[originTeamName] stamp a
 * traded or compensatory pick.
 */
@Serializable
data class PickDetail(
  val position: Int,
  val round: Int,
  val teamId: String,
  val teamName: String,
  val ownerName: String,
  val playerText: String? = null,
  val playerId: String? = null,
  val pickedAt: String? = null,
  val originTeamName: String? = null,
  val origin: PickOrigin? = null,
  val skipped: Boolean = false,
  val timedOutAt: String? = null,
  val editedAt: String? = null,
  val editedBy: String? = null,
) {
  val filled: Boolean get() = playerText != null || playerId != null
  val resolved: Boolean get() = filled || skipped
}

/** The pick currently on the clock (lowest unresolved, non-skipped position), with its team/owner resolved. */
@Serializable
data class OnClockInfo(
  val position: Int,
  val round: Int,
  val teamId: String,
  val teamName: String,
  val ownerName: String,
  val sinceIso: String? = null,
  val deadlineIso: String? = null,
  // Overnight pauses between the server's "now" and [deadlineIso], resolved backend-side; the
  // countdown subtracts them to show active time remaining.
  val quietWindows: List<QuietWindowInfo> = emptyList(),
)

/** Frontend mirror of the jvmMain `QuietWindowInfo` — one overnight pause, `[startIso, endIso)`. */
@Serializable
data class QuietWindowInfo(
  val startIso: String,
  val endIso: String,
)

/**
 * Frontend mirror of the (extended) jvmMain `MakePickRequest`. Exactly one of [playerId] /
 * [playerText] is non-null; the draft room always takes the free-text path.
 */
@Serializable
data class MakePickRequest(
  val draftId: String,
  val position: Int,
  val playerId: String? = null,
  val playerText: String? = null,
  // Draft a player Fantrax says is already rostered in the league. Honored only for callers who
  // can administer the draft; the server re-checks rather than trusting this flag.
  val overrideOwnership: Boolean = false,
)

/** Frontend mirror of the jvmMain `MakePickResponse`. */
@Serializable
data class MakePickResponse(
  val pick: PickResponse,
  val draftStatus: DraftStatus,
  // True when the entry was off-pool: the pick is held for a manager/admin ruling and the draft pauses.
  val pending: Boolean = false,
)

/** Frontend mirror of the jvmMain `ReviewPickRequest` — approve locks it in, reject sends it back. */
@Serializable
data class ReviewPickRequest(
  val draftId: String,
  val position: Int,
  val approve: Boolean,
)

@Serializable
data class EditPickRequest(
  val draftId: String,
  val position: Int,
  val playerText: String,
)

/**
 * Frontend mirror of the jvmMain `ManagePickRequest`. Each nullable field means "leave this alone",
 * so the board's manage-pick sheet sends only the controls the manager actually touched.
 */
@Serializable
data class ManagePickRequest(
  val draftId: String,
  val position: Int,
  val playerText: String? = null,
  val teamId: String? = null,
  val skipped: Boolean? = null,
  val overrideOwnership: Boolean = false,
)

/** Frontend mirror of the jvmMain `ManagePickResponse`. */
@Serializable
data class ManagePickResponse(
  val pick: PickResponse,
  val draftStatus: DraftStatus,
  val pending: Boolean = false,
)

/** Frontend mirror of the jvmMain `SkipPickRequest`. */
@Serializable
data class SkipPickRequest(
  val draftId: String,
  val position: Int,
)

/** Frontend mirror of the jvmMain `PickResponse`. */
@Serializable
data class PickResponse(
  val id: String,
  val position: Int,
  val teamId: String,
  val playerId: String? = null,
  val playerText: String? = null,
  val pickedAt: String? = null,
  val round: Int? = null,
  val originTeamId: String? = null,
  val origin: PickOrigin? = null,
  val skipped: Boolean = false,
  val timedOutAt: String? = null,
  val pendingText: String? = null,
  val pendingEnteredAt: String? = null,
)

/** Frontend mirror of the (new) jvmMain `NudgeRequest`. */
@Serializable
data class NudgeRequest(
  val draftId: String,
)
